import { isResourceStateProcessing } from '../utils/isResourceStateProcessing.js';
import { isWoodyError } from '../../errors/errors.utils.js';
import { useQueryClient, useQuery, keepPreviousData } from '@tanstack/react-query';
import { resourceQueryKeys } from './resourceQueryKeys.js';
import { useWoody } from '../../woody/woody.js';
import { isQueryEnabled } from '@fabric-app/queries/utilities';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { useSocketIOEventListener } from '../../socket.io/GeneralRoom/useSocketIOEventListener.js';
import { useCallback } from 'react';
import { resourceQueryPredicates } from './resourceQueryPredicates.js';

const getRefetchInterval = (resource) => {
  if (!resource) {
    return 45e3;
  }
  if (
    // If it's a notepad it has more potential to change
    // note: note sure if it's relevant with yjs?
    resource.kind === "notepad"
  ) return 15e3;
  if (isResourceStateProcessing(resource.stateProcessing) || "data" in resource && resource.data && "statePreview" in resource.data && // incorrect typing
  isResourceStateProcessing(resource.data.statePreview)) {
    return 5e3;
  }
  return 45e3;
};
const getIdAndTokenFromParams = (params) => {
  if (!params) {
    return {
      resourceId: null,
      accessToken: void 0,
      includeHidden: void 0
    };
  }
  if (typeof params === "string") {
    return {
      resourceId: params,
      accessToken: void 0,
      includeHidden: void 0
    };
  }
  return params;
};
const useQueryResourceDetail = (params, queryOptions) => {
  const { client } = useWoody();
  const queryClient = useQueryClient();
  const contextAccessToken = useContextAccessToken();
  const { resourceId, accessToken, includeHidden } = getIdAndTokenFromParams(params);
  const query = useQuery({
    queryKey: resourceQueryKeys.resourceDetail(resourceId),
    queryFn: async () => {
      const res = await client.v2({
        endpoint: "/v2/resources/{resourceId}",
        params: {
          resourceId
        }
      }, {
        query: {
          accessToken: accessToken ?? contextAccessToken,
          includeHidden: includeHidden ?? true
        }
      });
      queryOptions?.onFetchSuccess?.(res);
      return res;
    },
    retry: (failureCount, error) => {
      if (isWoodyError(error) && [
        404,
        401,
        403
      ].includes(error.status) || failureCount >= 3) {
        return false;
      }
      return true;
    },
    placeholderData: (r) => {
      const keepPrevious = keepPreviousData(r);
      if (keepPrevious) {
        return keepPrevious;
      }
      if (resourceId) {
        const data = queryClient.getQueriesData({
          predicate: resourceQueryPredicates.resourceListAny,
          type: "active"
        });
        for (const [_queryKey, queryData] of data) {
          if (queryData) {
            for (const page of queryData.pages) {
              for (const resource of page.resources) {
                if (resource.id === resourceId) {
                  return resource;
                }
              }
            }
          }
        }
      }
      return keepPreviousData(r);
    },
    refetchOnMount: true,
    refetchInterval: (query2) => getRefetchInterval(query2.state.data),
    ...queryOptions,
    enabled: isQueryEnabled([
      !!resourceId,
      queryOptions?.enabled
    ])
  });
  useSocketIOEventListener("RESOURCE:METADATA_ATTRIBUTES:UPDATE", (socketData) => {
    if (socketData.resourceId === resourceId) {
      const queryKey = resourceQueryKeys.resourceDetail(resourceId);
      queryClient.setQueryData(queryKey, (prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          data: "data" in prevData ? {
            ...prevData.data,
            attributes: socketData.attributes
          } : {
            attributes: socketData.attributes
          }
        };
      });
    }
  }, {
    enabled: !!resourceId
  });
  return {
    ...query,
    resource: query.data
  };
};
const useSetQueryResourceDetailData = () => {
  const queryClient = useQueryClient();
  return useCallback((resource, options) => {
    queryClient.setQueryData(resourceQueryKeys.resourceDetail(resource.id), (prevData) => {
      if (!prevData) return resource;
      if (options?.overwrite) {
        return resource;
      }
      return prevData;
    });
  }, [
    queryClient
  ]);
};

export { useQueryResourceDetail, useSetQueryResourceDetailData };
//# sourceMappingURL=useQueryResourceDetail.js.map
